import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const from = location.state?.from || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(form.email, form.password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Bienvenue, ${res.user.name}`);
      const dest = res.user.role === "admin" || res.user.role === "superadmin" ? "/admin" : from;
      navigate(dest, { replace: true });
    } else {
      toast.error(res.error || "Connexion impossible");
    }
  };

  return (
    <div className="min-h-[calc(100vh-12rem)] flex items-center bg-bone">
      <div className="container-jamm grid md:grid-cols-2 gap-12 items-center py-16">
        <div className="hidden md:block">
          <div className="overline">Bon retour</div>
          <h1 className="font-display text-5xl text-ink mt-4 leading-tight">On vous écoute,<br /><span className="italic text-sage-600">à nouveau.</span></h1>
          <p className="lead mt-5 max-w-md">Reprenez votre conversation, retrouvez votre accompagnateur, ou planifiez une nouvelle séance.</p>
          <img src="https://images.unsplash.com/photo-1550487810-d2114be89741?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" alt="" className="mt-10 rounded-3xl w-full h-72 object-cover" />
        </div>

        <form onSubmit={submit} className="card-soft max-w-md w-full mx-auto md:mx-0" data-testid="login-form">
          <h2 className="font-display text-3xl text-ink">Se connecter</h2>
          <p className="text-ink-soft mt-1 text-sm">Entrez vos informations pour continuer.</p>

          <label className="block mt-6 text-sm text-ink-soft">Email</label>
          <div className="mt-1.5 flex items-center rounded-xl border border-border bg-white px-3 focus-within:ring-2 focus-within:ring-sage-300">
            <Mail className="w-4 h-4 text-ink-mute" />
            <input
              required type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="flex-1 px-3 py-3 bg-transparent focus:outline-none"
              placeholder="vous@email.com"
              data-testid="login-email"
            />
          </div>

          <label className="block mt-4 text-sm text-ink-soft">Mot de passe</label>
          <div className="mt-1.5 flex items-center rounded-xl border border-border bg-white px-3 focus-within:ring-2 focus-within:ring-sage-300">
            <Lock className="w-4 h-4 text-ink-mute" />
            <input
              required type={showPwd ? "text" : "password"} value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="flex-1 px-3 py-3 bg-transparent focus:outline-none"
              placeholder="••••••••"
              autoComplete="current-password"
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="p-2 -mr-2 text-ink-mute hover:text-ink-soft transition rounded-lg"
              aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              tabIndex={-1}
              data-testid="login-toggle-password"
            >
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-6" data-testid="login-submit">
            {loading ? "Connexion…" : (<>Se connecter <ArrowRight className="w-4 h-4" /></>)}
          </button>

          <div className="text-right mt-3 text-sm">
            <Link to="/mot-de-passe-oublie" className="text-sage-600 font-medium hover:underline" data-testid="login-to-forgot">
              Mot de passe oublié ?
            </Link>
          </div>

          <div className="text-center mt-5 text-sm text-ink-soft">
            Pas de compte ? <Link to="/register" className="text-sage-600 font-medium hover:underline" data-testid="login-to-register">Créer un compte</Link>
          </div>
          <div className="text-xs text-ink-mute mt-4 bg-beige/60 rounded-lg p-3 leading-relaxed">
            Démo client : <strong>demo@jammsupport.com</strong> · <strong>Demo@Jamm2026</strong>
          </div>
        </form>
      </div>
    </div>
  );
}
